import { defineStore } from 'pinia'
import api from '@/api/axios'
import { ref, computed } from 'vue'

export const useRiskReportStore = defineStore('riskReport', () => {
  // State
  const riskReport = ref(null)
  const reportStatus = ref(null)
  const loading = ref(false)
  const error = ref(null)
  const generating = ref(false)
  const verificationResults = ref(null)
  
  // Computed getters
  const hasRiskReport = computed(() => !!riskReport.value)
  const reportContent = computed(() => riskReport.value?.content || {})
  const reportQuality = computed(() => riskReport.value?.quality || {})
  const reportMetadata = computed(() => riskReport.value?.metadata || {})
  const fullReport = computed(() => reportContent.value.fullReport || '')
  const executiveSummary = computed(() => reportContent.value.executiveSummary || '')
  const reportSections = computed(() => reportContent.value.sections || [])
  const footnotes = computed(() => reportContent.value.footnotes || [])
  
  // Status computed properties
  const isCompleted = computed(() => riskReport.value?.status === 'completed')
  const isUnderReview = computed(() => riskReport.value?.status === 'under_review')
  const isApproved = computed(() => riskReport.value?.status === 'approved')
  const isGenerating = computed(() => riskReport.value?.status === 'generating' || generating.value)
  const isRejected = computed(() => riskReport.value?.status === 'rejected')
  
  // Quality computed properties
  const qualityScore = computed(() => reportQuality.value.overallScore || 0)
  const qualityRating = computed(() => reportQuality.value.rating || 'unknown')
  const footnoteCoverage = computed(() => reportQuality.value.footnoteCoverage || '0%')
  const verifiedSources = computed(() => reportQuality.value.verifiedSources || 0)
  const totalSources = computed(() => reportQuality.value.totalSources || 0)
  
  // Metadata computed properties
  const wordCount = computed(() => reportMetadata.value.wordCount || 0)
  const sectionCount = computed(() => reportMetadata.value.sectionCount || 0)
  const processingTime = computed(() => reportMetadata.value.processingTime || 0)
  
  // Report type utilities
  const reportTypeOptions = [
    { value: 'comprehensive', label: 'Comprehensive Report', description: 'Complete investigative due diligence report' },
    { value: 'compliance_summary', label: 'Compliance Summary', description: 'Focused on regulatory compliance issues' },
    { value: 'executive_brief', label: 'Executive Brief', description: 'High-level summary for decision makers' },
    { value: 'regulatory_focus', label: 'Regulatory Focus', description: 'Emphasis on regulatory and legal risks' }
  ]
  
  const getReportTypeLabel = (reportType) => {
    const option = reportTypeOptions.find(opt => opt.value === reportType)
    return option?.label || reportType
  }
  
  // Quality utilities
  const getQualityColor = (rating) => {
    const colors = {
      excellent: 'text-green-700 bg-green-50 border-green-200 dark:text-green-400 dark:bg-green-900/20 dark:border-green-800',
      good: 'text-blue-700 bg-blue-50 border-blue-200 dark:text-blue-400 dark:bg-blue-900/20 dark:border-blue-800',
      fair: 'text-yellow-700 bg-yellow-50 border-yellow-200 dark:text-yellow-400 dark:bg-yellow-900/20 dark:border-yellow-800',
      poor: 'text-red-700 bg-red-50 border-red-200 dark:text-red-400 dark:bg-red-900/20 dark:border-red-800'
    }
    return colors[rating] || 'text-gray-700 bg-gray-50 border-gray-200 dark:text-gray-400 dark:bg-gray-900/20 dark:border-gray-800'
  }

  // API Functions
  
  /**
   * Check report status
   */
  async function checkReportStatus(projectId, sessionId) {
    loading.value = true
    error.value = null
    
    try {
      const response = await api.get(`/risk-reports/projects/${projectId}/sessions/${sessionId}/status`)
      
      if (response.data.success) {
        reportStatus.value = response.data.data
        return response.data.data
      } else {
        throw new Error(response.data.error || 'Failed to check report status')
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to check report status'
      error.value = errorMessage
      console.error('Failed to check report status:', err)
      throw new Error(errorMessage)
    } finally {
      loading.value = false
    }
  }
  
  /**
   * Generate risk report
   */
  async function generateRiskReport(projectId, sessionId, options = {}) {
    if (!sessionId) {
      throw new Error('Session ID is required')
    }
    
    generating.value = true
    loading.value = true
    error.value = null
    
    try {
      const requestPayload = {
        reportType: options.reportType || 'comprehensive',
        forceRegenerate: options.forceRegenerate || false
      }
      
      console.log('Generating risk report with payload:', requestPayload)
      
      const response = await api.post(
        `/risk-reports/projects/${projectId}/sessions/${sessionId}`,
        requestPayload
      )
      
      console.log('Generate risk report response:', {
        status: response.status,
        data: response.data,
        dataKeys: response.data ? Object.keys(response.data) : 'no data'
      })
      
      if (response.data?.success) {
        const reportData = response.data.report
        
        if (reportData && (reportData.content || reportData.status)) {
          riskReport.value = reportData
          console.log('Risk report generated successfully:', {
            id: reportData.id,
            entityName: reportData.entityName,
            reportType: reportData.reportType,
            status: reportData.status,
            qualityScore: reportData.quality?.overallScore,
            sectionCount: reportData.content?.sections?.length || 0,
            wordCount: reportData.metadata?.wordCount || 0
          })
          return { report: reportData }
        } else {
          throw new Error('No report data found in response')
        }
      } else {
        throw new Error(response.data?.error || 'Failed to generate risk report')
      }
    } catch (err) {
      const status = err.response?.status
      const errorData = err.response?.data
      
      let errorMessage = 'Failed to generate risk report'
      
      console.log('Generate risk report error:', {
        status,
        errorData,
        sessionId,
        originalError: err.message
      })
      
      if (status === 400) {
        if (errorData?.code === 'RISK_ANALYSIS_REQUIRED') {
          errorMessage = 'Risk analysis must be completed before generating report. Please complete risk analysis first.'
        } else {
          errorMessage = errorData?.error || 'Bad request'
        }
      } else if (status === 404) {
        errorMessage = 'Session not found or access denied'
      } else if (status === 429) {
        errorMessage = 'Too many report generation requests. Please try again later.'
      } else {
        errorMessage = errorData?.error || err.message || errorMessage
      }
      
      error.value = errorMessage
      console.error('Failed to generate risk report:', {
        status,
        code: errorData?.code,
        error: errorData?.error || err.message,
        sessionId
      })
      
      throw new Error(errorMessage)
    } finally {
      generating.value = false
      loading.value = false
    }
  }
  
  /**
   * Get existing risk report
   */
  async function getRiskReport(projectId, sessionId) {
    if (!sessionId) {
      throw new Error('Session ID is required')
    }
    
    loading.value = true
    error.value = null
    
    try {
      const response = await api.get(`/risk-reports/projects/${projectId}/sessions/${sessionId}`)
      
      console.log('Risk report response:', {
        status: response.status,
        data: response.data,
        dataKeys: response.data ? Object.keys(response.data) : 'no data'
      })
      
      if (response.data?.success) {
        const reportData = response.data?.report
        
        if (reportData && (reportData.content || reportData.status)) {
          riskReport.value = reportData
          console.log('Risk report loaded:', {
            id: reportData.id,
            entityName: reportData.entityName,
            reportType: reportData.reportType,
            status: reportData.status,
            qualityScore: reportData.quality?.overallScore,
            sectionCount: reportData.content?.sections?.length || 0,
            wordCount: reportData.metadata?.wordCount || 0
          })
          return { report: reportData }
        } else {
          throw new Error('No report data found in response')
        }
      } else {
        throw new Error(response.data?.error || 'Failed to fetch risk report')
      }
    } catch (err) {
      const status = err.response?.status
      const errorData = err.response?.data
      
      console.log('Risk report error:', {
        status,
        errorData,
        sessionId,
        originalError: err.message
      })
      
      if (status === 404) {
        if (errorData?.code === 'REPORT_NOT_FOUND' || errorData?.message?.includes('not found')) {
          console.log('No risk report found for session:', sessionId)
          riskReport.value = null
          return null
        } else {
          throw new Error('Session not found or access denied')
        }
      }
      
      const errorMessage = errorData?.error || err.message || 'Failed to fetch risk report'
      error.value = errorMessage
      console.error('Failed to fetch risk report:', err)
      throw new Error(errorMessage)
    } finally {
      loading.value = false
    }
  }
  
  /**
   * Regenerate risk report
   */
  async function regenerateRiskReport(projectId, sessionId, options = {}) {
    if (!sessionId) {
      throw new Error('Session ID is required')
    }
    
    generating.value = true
    loading.value = true
    error.value = null
    
    try {
      const requestPayload = {
        reportType: options.reportType || riskReport.value?.reportType || 'comprehensive'
      }
      
      console.log('Regenerating risk report with payload:', requestPayload)
      
      const response = await api.put(
        `/risk-reports/projects/${projectId}/sessions/${sessionId}/regenerate`,
        requestPayload
      )
      
      console.log('Regenerate risk report response:', {
        status: response.status,
        data: response.data,
        dataKeys: response.data ? Object.keys(response.data) : 'no data'
      })
      
      if (response.data?.success) {
        const reportData = response.data.report

        if (reportData && (reportData.content || reportData.status)) {
          riskReport.value = reportData
          console.log('Risk report loaded:', {
            id: reportData.id,
            entityName: reportData.entityName,
            reportType: reportData.reportType,
            status: reportData.status,
            qualityScore: reportData.quality?.overallScore,
            sectionCount: reportData.content?.sections?.length || 0,
            wordCount: reportData.metadata?.wordCount || 0
          })
          return { report: reportData }
        } else {
          throw new Error('No report data found in response')
        }
      } else {
        throw new Error(response.data?.error || 'Failed to fetch risk report')
      }
    } catch (err) {
      const status = err.response?.status
      const errorData = err.response?.data
      
      let errorMessage = 'Failed to regenerate risk report'
      
      console.log('Regenerate risk report error:', {
        status,
        errorData,
        sessionId,
        originalError: err.message
      })
      
      if (status === 400) {
        if (errorData?.code === 'RISK_ANALYSIS_REQUIRED') {
          errorMessage = 'Risk analysis must be completed before regenerating report. Please complete risk analysis first.'
        } else {
          errorMessage = errorData?.error || 'Bad request'
        }
      } else if (status === 404) {
        errorMessage = 'Session not found or access denied'
      } else if (status === 429) {
        errorMessage = 'Too many report regeneration requests. Please try again later.'
      } else {
        errorMessage = errorData?.error || err.message || errorMessage
      }
      
      error.value = errorMessage
      console.error('Failed to regenerate risk report:', {
        status,
        code: errorData?.code,
        error: errorData?.error || err.message,
        sessionId
      })
      
      throw new Error(errorMessage)
    } finally {
      generating.value = false
      loading.value = false
    }
  }
  
  /**
   * Verify report citations
   */
  async function verifyCitations(projectId, sessionId) {
    if (!sessionId) {
      throw new Error('Session ID is required')
    }
    
    loading.value = true
    error.value = null
    
    try {
      const response = await api.post(`/risk-reports/projects/${projectId}/sessions/${sessionId}/verify-citations`)
      
      if (response.data?.success) {
        verificationResults.value = response.data.data
        return response.data.data
      } else {
        throw new Error(response.data?.error || 'Failed to verify citations')
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to verify citations'
      error.value = errorMessage
      console.error('Failed to verify citations:', err)
      throw new Error(errorMessage)
    } finally {
      loading.value = false
    }
  }
  
  /**
   * Update report review status
   */
  async function updateReportReview(projectId, sessionId, reviewData) {
    if (!sessionId) {
      throw new Error('Session ID is required')
    }
    
    loading.value = true
    error.value = null
    
    try {
      const response = await api.patch(
        `/risk-reports/projects/${projectId}/sessions/${sessionId}/review`,
        reviewData
      )
      
      if (response.data?.success) {
        const reportData = response.data.data?.report || response.data.data
        if (reportData) {
          riskReport.value = reportData
        }
        return response.data.data
      } else {
        throw new Error(response.data?.error || 'Failed to update report review')
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to update report review'
      error.value = errorMessage
      console.error('Failed to update report review:', err)
      throw new Error(errorMessage)
    } finally {
      loading.value = false
    }
  }
  
  /**
   * Delete risk report
   */
  async function deleteRiskReport(projectId, sessionId) {
    if (!sessionId) {
      throw new Error('Session ID is required')
    }
    
    loading.value = true
    error.value = null
    
    try {
      const response = await api.delete(`/risk-reports/projects/${projectId}/sessions/${sessionId}`)
      
      if (response.data?.success) {
        riskReport.value = null
        reportStatus.value = null
        console.log('Risk report deleted successfully')
        return response.data.data
      } else {
        throw new Error(response.data?.error || 'Failed to delete risk report')
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to delete risk report'
      error.value = errorMessage
      console.error('Failed to delete risk report:', err)
      throw new Error(errorMessage)
    } finally {
      loading.value = false
    }
  }
  
  // Utility functions
  function clearRiskReport() {
    riskReport.value = null
    reportStatus.value = null
    verificationResults.value = null
    error.value = null
  }
  
  function formatDate(dateString) {
    if (!dateString) return 'Unknown'
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }
  
  function formatProcessingTime(timeMs) {
    if (!timeMs) return 'Unknown'
    const seconds = Math.round(timeMs / 1000)
    if (seconds < 60) return `${seconds}s`
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes}m ${remainingSeconds}s`
  }

  return {
    // State
    riskReport,
    reportStatus,
    verificationResults,
    loading,
    error,
    generating,
    
    // Computed
    hasRiskReport,
    reportContent,
    reportQuality,
    reportMetadata,
    fullReport,
    executiveSummary,
    reportSections,
    footnotes,
    isCompleted,
    isUnderReview,
    isApproved,
    isGenerating,
    isRejected,
    qualityScore,
    qualityRating,
    footnoteCoverage,
    verifiedSources,
    totalSources,
    wordCount,
    sectionCount,
    processingTime,
    
    // Utility functions
    reportTypeOptions,
    getReportTypeLabel,
    getQualityColor,
    formatDate,
    formatProcessingTime,
    
    // API functions
    checkReportStatus,
    generateRiskReport,
    getRiskReport,
    regenerateRiskReport,
    verifyCitations,
    updateReportReview,
    deleteRiskReport,
    
    // Local utilities
    clearRiskReport
  }
})